/**
 * Lint for customer CSS against the knobs contract (spec
 * docs/superpowers/specs/2026-10-03-stellschrauben-design.md §8, manifest
 * src/editor-v2/tokens/wm.knobs.json). Warns, never blocks.
 *
 * Plain JS (not TS) because the Vue-2 admin imports it (load it there with a
 * dynamic import, postcss is big). No JSON imports either: the caller passes
 * the two manifests, so node (tools/css/knobs.mjs) loads this file as is.
 * Types live in knobsLint.d.ts.
 */
// postcss is a devDependency on purpose: the admin bundle ships it, the build installs devDependencies anyway.
// eslint-disable-next-line import/no-extraneous-dependencies
import postcss from 'postcss';

const INNER_CLASS = /\.wm-(cell|group|el|widget|entry)(?![\w-])/;
const INNER_ATTR = /\[\s*data-(key|el|part)(?![\w-])/;
const ANY_WM_CLASS = /\.wm-(?!section(?![\w-]))[\w-]+/;
const SECTION = /\.wm-section(?![\w-])/;
const MARKER = /^wmk-ausnahme(-start|-ende)?\s*(?::([\s\S]*))?$/;
const GLOBAL_VALUE = /^(inherit|initial|unset|revert|revert-layer)$/i;
const FN = /^(var|calc|clamp|min|max|env)\(/i;
const LENGTH = /^(0|-?(\d+\.?\d*|\.\d+)(px|rem|em|%|vh|vw|vmin|vmax|svh|dvh|lvh|ch|ex|cqw|cqh|cqi|cqb|pt))$/i;
const COLOR = /^(#[0-9a-f]{3,8}|(rgba?|hsla?|hwb|lab|lch|oklab|oklch|color|color-mix|light-dark)\(.*\)|[a-z]+)$/i;
const GRADIENT = /^(repeating-)?(linear|radial|conic)-gradient\(|^url\(|^image-set\(/i;

/** Flat view of wm.tokens.json: every token with its dot path, CSS name and source. */
export function flatTokens(tokensJson) {
  const out = [];
  const walk = (node, path) => {
    if (!(node instanceof Object)) return; // leaves of the DTCG tree
    const ext = node.$extensions && node.$extensions['io.webmag'];
    if (ext && ext.cssVar) {
      out.push({
        manifestPath: path, cssVar: ext.cssVar, source: ext.source, catalogOnly: !!ext.catalogOnly,
      });
    }
    Object.keys(node).filter((k) => !k.startsWith('$')).forEach((k) => walk(node[k], path ? `${path}.${k}` : k));
  };
  walk(tokensJson, '');
  return out;
}

/**
 * Every name of the contract with what the lint needs to know about it.
 * `public`: name -> { from: 'token'|'hook'|'knob', kind, applies_when, type, note, pairs }.
 */
export function knobsVocabulary(knobsJson, tokensJson) {
  const tokens = flatTokens(tokensJson);
  const under = (path) => [...new Set(tokens
    .filter((t) => t.manifestPath === path || t.manifestPath.startsWith(`${path}.`)).map((t) => t.cssVar))];
  const pub = new Map();
  const add = (name, info) => { if (!pub.has(name)) pub.set(name, info); };
  knobsJson.tokens.forEach((t) => {
    const pairs = (t.pairs || []).flatMap(under);
    const info = {
      from: 'token', kind: t.kind, applies_when: t.applies_when, type: null, note: t.note, pairs, group: t.ref || t.name,
    };
    let names;
    if (t.name) names = [t.name];
    else if (t.pattern) {
      const roles = [...new Set([...Object.values(t.roles.heading), ...Object.values(t.roles.text)])];
      names = roles.flatMap((r) => t.props.map((p) => t.pattern.replace('<role>', r).replace('<prop>', p)));
    } else {
      const omit = (t.omit || []).flatMap(under);
      names = under(t.ref).filter((n) => !omit.includes(n));
    }
    names.forEach((n) => add(n, info));
    // A derived pair may be set next to its source (spec §4), so it is public too.
    pairs.forEach((n) => add(n, { ...info, pairs: [] }));
  });
  knobsJson.hooks.forEach((h) => add(h.name, {
    from: 'hook', kind: h.kind, applies_when: h.applies_when, type: null, note: h.note, pairs: [], group: 'hooks',
  }));
  knobsJson.knobs.forEach((k) => add(k.name, {
    from: 'knob', kind: k.kind, applies_when: k.applies_when, type: k.type, note: k.description, pairs: [], group: 'knobs',
  }));
  const notPublic = new Set(tokens.filter((t) => !t.catalogOnly && !pub.has(t.cssVar)).map((t) => t.cssVar));
  const legacy = knobsJson.legacy.map((l) => ({ ...l, names: l.pattern.match(/--wm-[a-z0-9-]+/g) || [] }));
  const removed = new Map(knobsJson.removed.map((r) => [r.name, r]));
  return {
    public: pub, notPublic, legacy, removed,
  };
}

/** Splits one selector into its compounds (top-level combinators only). */
function compounds(selector) {
  const out = [];
  let cur = '';
  let depth = 0;
  let quote = '';
  [...selector].forEach((c) => {
    if (quote) { cur += c; if (c === quote) quote = ''; return; }
    if (c === '"' || c === "'") quote = c;
    else if (c === '(' || c === '[') depth += 1;
    else if (c === ')' || c === ']') depth -= 1;
    if (depth === 0 && /[\s>+~]/.test(c)) { if (cur) out.push(cur); cur = ''; return; }
    cur += c;
  });
  if (cur) out.push(cur);
  // Functional pseudo-classes (:not, :where, :has) do not move the target.
  return out.map((s) => s.replace(/\((?:[^()]|\([^()]*\))*\)/g, '()'));
}

/** 'bereich' (targets a Bereich, S6), 'inner' (reaches into the parts, W1) or 'outside' (no v3 markup). */
export function classifySelector(selector) {
  const parts = compounds(selector);
  const first = parts.findIndex((p) => SECTION.test(p));
  const inner = (p) => INNER_CLASS.test(p) || INNER_ATTR.test(p);
  if (first === -1) return parts.some(inner) ? 'inner' : 'outside';
  if (parts.slice(0, first).some(inner)) return 'inner';
  return parts.slice(first).every((p) => SECTION.test(p) && !inner(p) && !ANY_WM_CLASS.test(p)) ? 'section' : 'inner';
}

/** Rough value check for a knob type (an invalid value means `unset` in the browser, not the fallback). */
export function valueFits(type, value) {
  const v = value.trim();
  if (!v || GLOBAL_VALUE.test(v) || FN.test(v)) return !!v;
  if (type === 'length') return LENGTH.test(v) || v === 'auto';
  if (type === 'lengths') return v.split(/\s+/).length <= 4 && v.split(/\s+/).every((x) => LENGTH.test(x) || FN.test(x) || x === 'auto');
  if (type === 'color') return COLOR.test(v) && !/gradient\(/i.test(v);
  if (type === 'background') return COLOR.test(v) || GRADIENT.test(v);
  return true;
}

function finding(level, code, node, selector, text, suggestion = null) {
  const line = node.source && node.source.start ? node.source.start.line : 0;
  // `prop`: the declaration's property, so a UI can word the finding itself (null for rule-level findings).
  return {
    level, code, line, selector, text, suggestion, prop: node.type === 'decl' ? node.prop : null,
  };
}

/** Exception reason for every rule that carries a marker (or null), plus W6 findings for markers without reason. */
function exceptions(root) {
  const reasons = new Map();
  const out = [];
  let block = null;
  root.walk((node) => {
    if (node.type === 'comment') {
      const m = node.text.trim().match(MARKER);
      if (!m) return;
      const reason = (m[2] || '').trim();
      if (m[1] !== '-ende' && !reason) out.push(finding('warn', 'W6', node, '', 'exception marker without a reason', '/* wmk-ausnahme: why */'));
      if (m[1] === '-start') block = reason || '?';
      else if (m[1] === '-ende') block = null;
      return;
    }
    if (node.type !== 'rule') return;
    const prev = node.prev();
    const own = prev && prev.type === 'comment' ? prev.text.trim().match(MARKER) : null;
    if (own && !own[1]) reasons.set(node, (own[2] || '').trim() || '?');
    else if (block) reasons.set(node, block);
  });
  return { reasons, out };
}

function inKeyframes(rule) {
  for (let p = rule.parent; p; p = p.parent) if (p.type === 'atrule' && /keyframes$/i.test(p.name)) return true;
  return false;
}

function legacyHit(vocab, prop, selector) {
  const hits = vocab.legacy.filter((l) => l.names.includes(prop));
  const exact = hits.find((l) => (l.pattern.match(/"[^"]+"/g) || []).some((q) => selector.includes(q.slice(1, -1))));
  if (exact || hits.length < 2) return exact || hits[0];
  return { use: hits.map((l) => l.use).join(' or ') }; // e.g. --wm-flaeche without data-flaeche in the selector
}

/**
 * Lints customer CSS. Returns findings `{ level: 'warn'|'info', code, line, selector, text, suggestion }`,
 * codes as in spec §8.1 (W1-W7, I1-I3, A; P = the CSS does not parse).
 */
export function lintKnobs(css, vocab) {
  let root;
  try {
    root = postcss.parse(css);
  } catch (e) {
    return [{
      level: 'warn', code: 'P', line: e.line || 0, selector: '', text: `CSS does not parse: ${e.reason || e.message}`, suggestion: null, prop: null,
    }];
  }
  const { reasons, out } = exceptions(root);
  root.walkRules((rule) => {
    if (inKeyframes(rule)) return;
    const sel = rule.selector;
    if (reasons.has(rule)) {
      out.push(finding('info', 'A', rule, sel, `exception: ${reasons.get(rule)}`));
      return;
    }
    const kinds = rule.selectors.map(classifySelector);
    const decls = rule.nodes.filter((n) => n.type === 'decl');
    const set = new Set(decls.map((d) => d.prop));
    let where = 'outside';
    if (kinds.includes('inner')) where = 'inner';
    else if (kinds.includes('section')) where = 'section';
    if (where === 'inner') {
      out.push(finding('warn', 'W1', rule, sel, 'reaches into the inner parts of a Bereich; set a variable on .wm-section[...] or mark an exception', '/* wmk-ausnahme: why */'));
    }
    decls.forEach((d) => {
      const { prop } = d;
      const pub = vocab.public.get(prop);
      const legacy = legacyHit(vocab, prop, sel);
      if (vocab.removed.has(prop)) {
        const r = vocab.removed.get(prop);
        out.push(finding('warn', 'W7', d, sel, `${prop} is deprecated (read until theme ${r.read_until})`, r.use));
      } else if (legacy) {
        out.push(finding('warn', 'W4', d, sel, legacy.use ? `old name ${prop}, use ${legacy.use} on the Bereich` : `old name ${prop}, no replacement in version 1`, legacy.use));
      } else if (prop === 'height' && /\[\s*data-el\s*=\s*["']?sponsor/.test(sel) && /\bimg\b/.test(sel)) {
        out.push(finding('warn', 'W4', d, sel, 'sponsor logo height: use --wmk-sponsor-logo-height on the Bereich', '--wmk-sponsor-logo-height'));
      } else if (where === 'outside') {
        if (pub) out.push(finding('warn', 'W3', d, sel, pub.from === 'token' ? `${prop} outside a Bereich: use the CI field` : `${prop} outside a Bereich: set it on .wm-section[...]`));
      } else if (where === 'section') {
        if (pub) {
          if (pub.type && !valueFits(pub.type, d.value)) out.push(finding('warn', 'W5', d, sel, `${prop}: "${d.value}" is not a ${pub.type}; an invalid value means unset, not the default`));
          if (pub.kind === 'meaning' && pub.applies_when) out.push(finding('info', 'I1', d, sel, `${prop} acts only when ${pub.applies_when}`));
          const missing = pub.pairs.filter((p) => !set.has(p));
          if (missing.length) out.push(finding('info', 'I2', d, sel, `${prop} does not recompute its derived values, set them too`, missing.join(', ')));
        } else if (prop.startsWith('--wmk-')) {
          out.push(finding('warn', 'W5', d, sel, `${prop} is not in the contract`));
        } else if (vocab.notPublic.has(prop)) {
          out.push(finding('info', 'I3', d, sel, `${prop} is not public; from a Bereich it may do nothing`));
        } else if (!prop.startsWith('--')) {
          out.push(finding('warn', 'W2', d, sel, `${prop} on the Bereich is not a contract variable`));
        }
      }
    });
  });
  return out.sort((a, b) => a.line - b.line);
}
